import React, { useEffect, useRef, useState } from "react"
import { Modal, ModalBody, ModalHeader, Button, Input, Alert } from "reactstrap"
import { fetchAvailableSlots, rescheduleAppointment } from "helpers/api_helper"
import { showMessage } from "components/Notification"
import AppointmentSlotSelector from "./AppointmentSlotSelector"
import AppointmentRescheduleSummary from "./AppointmentRescheduleSummary"

const toDateInputValue = value => {
  if (!value) return ""
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ""
  return date.toISOString().slice(0, 10)
}

const normalizeTime = value =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "")

const getFriendlyRescheduleError = response => {
  const statusCode = response?.error?.status
  const message = String(response?.message || "").toLowerCase()

  if (statusCode === 401 || statusCode === 403 || message.includes("unauthor")) {
    return "You are not authorized to reschedule this appointment."
  }
  if (statusCode === 404 || message.includes("not found")) {
    return "This appointment could not be found. Please refresh and try again."
  }
  if (
    statusCode === 409 ||
    message.includes("slot") ||
    message.includes("unavailable") ||
    message.includes("already booked")
  ) {
    return "That time is no longer available. Please select another slot."
  }
  if (message.includes("completed") || message.includes("cancel")) {
    return "Completed or cancelled appointments cannot be rescheduled."
  }
  if (message.includes("past")) {
    return "Please select a future appointment date and time."
  }
  if (statusCode === 400 || statusCode === 422 || message.includes("invalid")) {
    return "The selected date or time is invalid. Please choose another slot."
  }
  if (!response || response?.error?.request) {
    return "We could not reach the server. Check your connection and try again."
  }
  return "Unable to reschedule the appointment. Please try again."
}

const RescheduleAppointmentModal = ({
  isOpen,
  onClose,
  appointmentData,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false)
  const [slotsLoading, setSlotsLoading] = useState(false)
  const [error, setError] = useState("")
  const [availableSlots, setAvailableSlots] = useState([])
  const [slotMessage, setSlotMessage] = useState("")
  const [selectedDate, setSelectedDate] = useState("")
  const [selectedTime, setSelectedTime] = useState("")
  const [selectedSlot, setSelectedSlot] = useState(null)
  const [reason, setReason] = useState("")
  const [slotsRefreshKey, setSlotsRefreshKey] = useState(0)
  const submittingRef = useRef(false)

  const appointmentStatus = (appointmentData?.status || "").toLowerCase()
  const isBlocked = ["completed", "cancelled", "canceled"].includes(appointmentStatus)
  const doctorId =
    appointmentData?.doctorId?._id ||
    appointmentData?.doctorId ||
    appointmentData?.vetId?._id ||
    appointmentData?.vetId ||
    ""

  useEffect(() => {
    if (!isOpen) return
    setSelectedDate(toDateInputValue(appointmentData?.date))
    setSelectedTime("")
    setSelectedSlot(null)
    setReason("")
    setError("")
    setSlotsRefreshKey(0)
  }, [isOpen, appointmentData?.date])

  useEffect(() => {
    if (!isOpen || !doctorId || !selectedDate) return
    let active = true
    const loadTimings = async () => {
      setSlotsLoading(true)
      setError("")
      try {
        const response = await fetchAvailableSlots({ doctorId, date: selectedDate })
        if (!active) return
        if (response?.status === 1) {
          const slots = response?.data?.slots || []
          setAvailableSlots(slots.map(slot => ({ ...slot, status: "AVAILABLE", available: true })))
          setSlotMessage(slots.length ? "" : "No available slots for the selected date.")
        } else {
          setAvailableSlots([])
          setError(response?.message || "Unable to load available slots.")
        }
      } catch (requestError) {
        if (!active) return
        setTimings([])
        setError(requestError?.response?.data?.message || "Unable to load available slots.")
      } finally {
        if (active) setSlotsLoading(false)
      }
    }
    loadTimings()
    return () => {
      active = false
    }
  }, [isOpen, doctorId, selectedDate, slotsRefreshKey])

  const handleSlotSelect = slot => {
    setSelectedSlot(slot)
    setSelectedTime(slot?.time || slot?.startTime || "")
    setError("")
  }

  const handleDateChange = event => {
    setSelectedDate(event.target.value)
  }

  const currentDate = toDateInputValue(appointmentData?.date)
  const isUnchanged =
    selectedDate === currentDate &&
    normalizeTime(selectedTime) === normalizeTime(appointmentData?.time)
  const isConfirmDisabled =
    loading ||
    slotsLoading ||
    isBlocked ||
    !selectedDate ||
    !selectedTime ||
    !selectedSlot ||
    isUnchanged

  const handleSubmit = async () => {
    if (submittingRef.current) return
    if (isBlocked) {
      setError("This appointment cannot be rescheduled.")
      return
    }
    if (!selectedSlot || !selectedDate || !selectedTime) {
      setError("Please select an available slot.")
      return
    }
    if (isUnchanged) {
      setError("Please select a different date or time.")
      return
    }
    const selectedSlotStatus = selectedSlot.status || selectedSlot.availabilityStatus
    if (selectedSlotStatus && selectedSlotStatus.toString().toUpperCase() !== "AVAILABLE") {
      setError("Selected slot is unavailable.")
      return
    }

    submittingRef.current = true
    setLoading(true)
    setError("")
    const payload = {
      appointmentId: appointmentData?._id,
      newDate: selectedDate,
      newTime: selectedTime,
      ...(reason.trim() ? { reason: reason.trim() } : {}),
    }
    try {
      const response = await rescheduleAppointment(appointmentData?._id, payload)
      if (response?.status === 1) {
        const updatedAppointment =
          response?.data?.appointment || response?.data?.updatedAppointment || response?.data
        if (
          updatedAppointment?._id &&
          updatedAppointment._id !== appointmentData?._id
        ) {
          setError("The server returned an unexpected appointment. Please refresh and try again.")
          return
        }
        showMessage({
          label: "Appointment successfully rescheduled.",
          status: "success",
        })
        await onSuccess?.(updatedAppointment)
        onClose?.()
      } else {
        const friendlyError = getFriendlyRescheduleError(response)
        setError(friendlyError)
        if (friendlyError.includes("no longer available")) {
          setSlotsRefreshKey(current => current + 1)
        }
      }
    } catch {
      setError("We could not reach the server. Check your connection and try again.")
    } finally {
      submittingRef.current = false
      setLoading(false)
    }
  }

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg" centered>
      <ModalHeader toggle={onClose}>Reschedule Appointment</ModalHeader>
      <ModalBody>
        {error ? <Alert color="danger">{error}</Alert> : null}
        {isBlocked ? <Alert color="warning">This appointment is completed or cancelled and cannot be rescheduled.</Alert> : null}
        <AppointmentRescheduleSummary appointmentData={appointmentData} />
        <div className="my-3">
          <label className="form-label">New Date</label>
          <Input
            type="date"
            min={toDateInputValue(new Date())}
            value={selectedDate}
            onChange={handleDateChange}
            disabled={loading || slotsLoading || isBlocked}
          />
        </div>
        <div className="my-3">
          <label className="form-label">Reason (optional)</label>
          <Input value={reason} onChange={e => setReason(e.target.value)} placeholder="Add a reason for reschedule" disabled={loading || isBlocked} />
        </div>
        {slotsLoading ? (
          <div className="text-muted border rounded p-3">Loading available slots...</div>
        ) : (
          <AppointmentSlotSelector
            availableSlots={availableSlots}
            selectedSlot={selectedSlot}
            onSelectSlot={handleSlotSelect}
            selectedDate={selectedDate}
            emptyMessage={slotMessage}
          />
        )}
        <div className="d-flex justify-content-end gap-2 mt-4">
          <Button color="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleSubmit} disabled={isConfirmDisabled}>
            {loading ? "Rescheduling..." : "Confirm Reschedule"}
          </Button>
        </div>
      </ModalBody>
    </Modal>
  )
}

export default RescheduleAppointmentModal
